/**
 * Who else has this programme open.
 *
 * @module components/editor/EditorPresence
 */

import { useEffect, useState } from "react";

import { useOptionalEntraAuth } from "@/auth/entra-auth-context";
import { avatarColourFor, FloatingTip, Icon, PresenceStack } from "@/components/ui";
import { profilePhotoUrl } from "@/platform/graph-photo";
import {
  type EditorPresenceState,
  type SelfPresence,
  useRealtime,
} from "@/platform/realtime-context";

/** A module's title by its id, or nothing when the caller cannot say. */
type ModuleTitle = ((moduleId: string) => string | undefined) | undefined;

/**
 * What an avatar stands for, in words.
 *
 * Initials collide far more often than names do, so the full name and what
 * they are doing carry the meaning for a screen reader and on hover.
 *
 * @param peer - The peer to describe.
 * @returns A sentence naming them and their activity.
 */
function describePeer(
  peer: SelfPresence,
  moduleTitle: ModuleTitle,
  who = `${peer.name} is`,
): string {
  const doing = peer.activity === "editing" ? "editing" : "viewing";
  if (!peer.moduleId) {
    return `${who} ${doing}`;
  }
  // Never the stored id: it names nothing a reader can find on the page.
  return `${who} ${doing} ${moduleTitle?.(peer.moduleId) ?? "a module"}`;
}

/**
 * Profile photos for the peers in the room, by user id.
 *
 * Optional auth: presence renders outside the provider in tests, and a
 * cookie-only session has no MSAL account to acquire from either. A peer with
 * no photo, or whose lookup fails, stays on initials.
 *
 * @param peers - The peers currently in the room.
 * @returns A photo URL per user id that has one.
 */
function usePeerPhotos(peers: readonly EditorPresenceState[]): Record<string, string> {
  const [photos, setPhotos] = useState<Record<string, string>>({});
  const getGraphToken = useOptionalEntraAuth()?.getGraphToken;
  const userIds = [...new Set(peers.map((peer) => peer.userId).filter(Boolean))].sort().join("\n");

  useEffect(() => {
    let live = true;
    if (!userIds || !getGraphToken) {
      return () => {
        live = false;
      };
    }
    userIds.split("\n").forEach((userId) => {
      profilePhotoUrl(getGraphToken, userId)
        .then((url) => {
          if (live && url) {
            setPhotos((current) => ({ ...current, [userId]: url }));
          }
        })
        .catch(() => undefined);
    });
    return () => {
      live = false;
    };
  }, [getGraphToken, userIds]);

  return photos;
}

/** A visible label for presence it cannot show, with why on hover and focus. */
function PresenceNotice({
  label,
  visible,
  showLabel,
  detail,
  icon,
  "data-testid": testId,
}: {
  /** The accessible name; it contains `visible` so the two agree. */
  label: string;
  visible: string;
  /** Spell the state out beside the icon, where there is room for it. */
  showLabel: boolean | "wide";
  detail: string;
  icon: string;
  "data-testid": string;
}): React.JSX.Element {
  return (
    <FloatingTip tip={`${label}. ${detail}`}>
      {(trigger) => (
        <button
          {...trigger}
          type="button"
          aria-label={label}
          data-testid={testId}
          className="text-muted inline-flex min-h-8 min-w-8 cursor-help items-center justify-center gap-1 text-md"
        >
          <Icon name={icon} aria-hidden />
          {showLabel === "wide" ? <span className="max-xl:hidden">{visible}</span> : null}
          {showLabel === true ? visible : null}
        </button>
      )}
    </FloatingTip>
  );
}

/** The people in the room as one stack of avatars, you first, each with its activity and its name on hover. */
function PeerStack({
  self,
  peers,
  moduleTitle,
}: {
  self: SelfPresence | undefined;
  peers: readonly EditorPresenceState[];
  moduleTitle: ModuleTitle;
}): React.JSX.Element {
  const photos = usePeerPhotos(self?.userId ? [...peers, { clientId: "self", ...self }] : peers);
  const person = (key: string, peer: SelfPresence, label: string, testId: string) => ({
    key,
    name: peer.name,
    label,
    src: peer.userId ? photos[peer.userId] : undefined,
    colour: avatarColourFor(peer.name),
    badge: <Icon name={peer.activity === "editing" ? "pencil-simple" : "eye"} aria-hidden />,
    "data-testid": testId,
    "data-activity": peer.activity,
  });
  const people = [
    ...(self
      ? [person("self", self, describePeer(self, moduleTitle, "You are"), "editor-presence-self")]
      : []),
    ...peers.map((peer) =>
      person(peer.clientId, peer, describePeer(peer, moduleTitle), "editor-presence-peer"),
    ),
  ];
  return (
    <PresenceStack
      people={people}
      max={people.length}
      aria-label={
        peers.length === 0
          ? "Only you in this programme"
          : `${String(people.length)} people in this programme`
      }
      data-testid="editor-presence"
    />
  );
}

/**
 * Render the people editing alongside you, or say honestly that we do not know.
 *
 * Never an empty list where the channel is not working: that reads as "nobody
 * else is here", which is exactly the state in which somebody confidently
 * starts editing. The two ignorant states are told apart because only one of
 * them is worth acting on, and telling a user whose connection dropped that
 * their environment does not report presence sends them the wrong way.
 *
 * @param props.showLabel - Name a degraded channel in words, not only by its icon; `"wide"` only from `xl` up.
 * @param props.showLabel - Name a degraded channel in words, not only by its icon.
 * @returns The presence row, which on a working channel always includes you.
 */
export function EditorPresence({
  moduleTitle = undefined,
  showLabel = false,
}: {
  moduleTitle?: ModuleTitle;
  showLabel?: boolean | "wide";
}): React.JSX.Element | null {
  const { status, peers, self } = useRealtime();

  if (status === "unavailable") {
    return (
      <PresenceNotice
        label="Live collaboration disconnected"
        showLabel={showLabel}
        visible="Collaboration disconnected"
        detail="This environment does not report who else is editing."
        icon="wifi-slash"
        data-testid="editor-presence-unknown"
      />
    );
  }

  if (status === "offline") {
    return (
      <PresenceNotice
        label="Reconnecting to live collaboration"
        showLabel={showLabel}
        visible="Reconnecting"
        detail="The connection to other editors was lost. Reconnecting. Reload the page if it does not come back."
        icon="wifi-low"
        data-testid="editor-presence-offline"
      />
    );
  }

  if (status === "failed") {
    return (
      <PresenceNotice
        label="Live collaboration disconnected"
        showLabel={showLabel}
        visible="Collaboration disconnected"
        detail="The connection to other editors could not be restored. Reload the page to try again."
        icon="wifi-slash"
        data-testid="editor-presence-failed"
      />
    );
  }

  // You are shown even alone: an empty header cannot be told apart from
  // presence that is not there at all.
  if (!self && peers.length === 0) {
    return null;
  }

  return <PeerStack self={self} peers={peers} moduleTitle={moduleTitle} />;
}
